import React from 'react'
import '../../styles/fonts.css'

export type FontFamily = 'geist' | 'geistMono'
export type FontSize =
  | 'displayLg'
  | 'displayMd'
  | 'displaySm'
  | 'headlineLg'
  | 'headlineMd'
  | 'headlineSm'
  | 'titleLg'
  | 'titleMd'
  | 'titleSm'
  | 'bodyLg'
  | 'bodyMd'
  | 'bodySm'
  | 'bodyLgMedium'
  | 'bodyMdMedium'
  | 'bodySmMedium'
  | 'labelLg'
  | 'labelMd'
  | 'labelSm'
  | 'bodyTag'
  | 'bodyTagLg'

export type FontWeight = 'normal' | 'medium' | 'semibold' | 'bold'

export type TextDecoration = 'none' | 'underline'

const FONT_FAMILIES: Record<FontFamily, React.CSSProperties> = {
  geist: {
    fontFamily: 'Geist, sans-serif'
  },
  geistMono: {
    fontFamily: 'Geist Mono, sans-serif'
  }
}

const FONT_SIZES: Record<FontSize, string> = {
  // Display sizes
  displayLg: '57px',
  displayMd: '45px',
  displaySm: '36px',

  // Headline sizes
  headlineLg: '32px',
  headlineMd: '28px',
  headlineSm: '24px',

  // Title sizes
  titleLg: '20px',
  titleMd: '16px',
  titleSm: '14px',

  // Body sizes - normal weight
  bodyLg: '16px',
  bodyMd: '14px',
  bodySm: '12px',

  // Body sizes - medium weight
  bodyLgMedium: '16px',
  bodyMdMedium: '14px',
  bodySmMedium: '12px',

  // Label sizes
  labelLg: '15px',
  labelMd: '12px',
  labelSm: '11px',

  // Body tag
  bodyTagLg: '13px',
  bodyTag: '10px'
}

const LINE_HEIGHTS: Record<FontSize, string> = {
  // Display line heights
  displayLg: '68.4px',
  displayMd: '54px',
  displaySm: '43.2px',

  // Headline line heights
  headlineLg: '38.4px',
  headlineMd: '33.6px',
  headlineSm: '28.8px',

  // Title line heights
  titleLg: '22px',
  titleMd: '24px',
  titleSm: '18px',

  // Body line heights - normal weight
  bodyLg: '20px',
  bodyMd: '16px',
  bodySm: '14px',

  // Body line heights - medium weight
  bodyLgMedium: '19.2px',
  bodyMdMedium: '22px',
  bodySmMedium: '14.4px',

  // Label line heights
  labelLg: '18px',
  labelMd: '14.4px',
  labelSm: '13.2px',

  // Body tag
  bodyTag: '12px',
  bodyTagLg: '15.6px'
}

const LETTER_SPACING: Record<FontSize, string> = {
  // Display letter spacing
  displayLg: '0em',
  displayMd: '0em',
  displaySm: '0em',

  // Headline letter spacing
  headlineLg: '0em',
  headlineMd: '0em',
  headlineSm: '0em',

  // Title letter spacing
  titleLg: '0em',
  titleMd: '0em',
  titleSm: '0em',

  // Body letter spacing - normal weight
  bodyLg: '0em',
  bodyMd: '0em',
  bodySm: '0em',

  // Body letter spacing - medium weight
  bodyLgMedium: '0em',
  bodyMdMedium: '0em',
  bodySmMedium: '0em',

  // Label letter spacing
  labelLg: '0em',
  labelMd: '0em',
  labelSm: '-0.03em', // -0.33px converted to em for 11px font size

  // Body tag
  bodyTag: '0em',
  bodyTagLg: '0em'
}

const FONT_WEIGHTS: Record<FontWeight, number> = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700
}

const TEXT_DECORATIONS: Record<TextDecoration, string> = {
  none: 'none',
  underline: 'underline'
}

export interface TypographyProps {
  fontFamily: FontFamily
  fontSize: FontSize
  fontWeight?: FontWeight
  textDecoration?: TextDecoration
  style?: React.CSSProperties
  className?: string
  children: React.ReactNode
  renderAs?: keyof JSX.IntrinsicElements
  onClick?: () => void
}

export const Typography: React.FC<TypographyProps> = ({
  fontFamily,
  fontSize,
  fontWeight,
  textDecoration = 'none',
  style,
  className,
  children,
  renderAs: Component = 'span',
  onClick
}) => {
  return (
    <Component
      onClick={onClick}
      className={className}
      style={{
        ...FONT_FAMILIES[fontFamily],
        fontSize: FONT_SIZES[fontSize],
        ...(fontWeight && { fontWeight: FONT_WEIGHTS[fontWeight] }),
        lineHeight: LINE_HEIGHTS[fontSize],
        letterSpacing: LETTER_SPACING[fontSize],
        textDecoration: TEXT_DECORATIONS[textDecoration],
        display: 'block',
        ...style
      }}
    >
      {children}
    </Component>
  )
}

export default Typography
